import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true, cannot be disabled
    functional: false,
    analytics: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Show banner after a brief delay
      setTimeout(() => setShowBanner(true), 1000);
    } else {
      // Load saved preferences
      try {
        const savedPrefs = JSON.parse(consent);
        setPreferences(savedPrefs);
      } catch (e) {
        console.error('Error parsing cookie consent:', e);
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const newPreferences = {
      essential: true,
      functional: true,
      analytics: true
    };
    setPreferences(newPreferences);
    saveConsent(newPreferences);
    setShowBanner(false);
  };

  const handleAcceptEssential = () => {
    const newPreferences = {
      essential: true,
      functional: false,
      analytics: false
    };
    setPreferences(newPreferences);
    saveConsent(newPreferences);
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
    setShowBanner(false);
    setShowDetails(false);
  };

  const saveConsent = (prefs) => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      ...prefs,
      timestamp: new Date().toISOString()
    }));

    // Initialize analytics if accepted
    if (prefs.analytics) {
      // Add your analytics initialization here
      // e.g., initializeGoogleAnalytics();
    }
  };

  const handleToggle = (key) => {
    if (key === 'essential') return; // Can't disable essential cookies
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!showBanner) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center pointer-events-none">
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black transition-opacity duration-300 pointer-events-auto ${
          showDetails ? 'opacity-40' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setShowDetails(false)}
      />

      {/* Cookie Banner */}
      <div className="relative w-full max-w-6xl mx-4 mb-4 pointer-events-auto">
        <div className="bg-white rounded-lg shadow-2xl border-2 border-teal-500 overflow-hidden">
          {/* Simple Banner View */}
          {!showDetails && (
            <div className="p-4 sm:p-6">
              <div className="flex items-start gap-4">
                {/* Cookie Icon */}
                <div className="flex-shrink-0 mt-1">
                  <svg className="w-8 h-8 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    🍪 We Value Your Privacy
                  </h3>
                  <p className="text-sm text-gray-700 mb-4">
                    We use cookies to enhance your experience, provide essential features, and analyze our traffic.
                    Essential cookies are required for the site to work. Optional cookies help us improve your experience.
                  </p>

                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                    <button
                      onClick={handleAcceptAll}
                      className="px-6 py-2.5 bg-teal-500 hover:bg-teal-600 text-white font-semibold rounded-lg transition-colors shadow-md hover:shadow-lg"
                    >
                      Accept All
                    </button>

                    <button
                      onClick={handleAcceptEssential}
                      className="px-6 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-lg transition-colors"
                    >
                      Essential Only
                    </button>

                    <button
                      onClick={() => setShowDetails(true)}
                      className="px-6 py-2.5 border-2 border-gray-300 hover:border-gray-400 text-gray-700 font-semibold rounded-lg transition-colors"
                    >
                      Customize
                    </button>
                  </div>

                  <p className="text-xs text-gray-600 mt-3">
                    By using WanderVibe, you agree to our use of cookies. Learn more in our{' '}
                    <Link to="/privacy" className="text-teal-600 hover:underline font-medium">
                      Privacy Policy
                    </Link>
                    .
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Detailed Settings View */}
          {showDetails && (
            <div className="p-6 max-h-[80vh] overflow-y-auto">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Cookie Preferences
                </h3>
                <p className="text-sm text-gray-700">
                  Manage your cookie settings. Essential cookies are always enabled to ensure the website functions properly.
                </p>
              </div>

              {/* Essential Cookies */}
              <div className="mb-6 pb-6 border-b border-gray-200">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-bold text-gray-900">Essential Cookies</h4>
                      <span className="px-2 py-0.5 bg-red-100 text-red-800 text-xs font-semibold rounded">
                        Required
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 mb-2">
                      These cookies are necessary for the website to function and cannot be disabled. They are usually only set in response to actions you take, such as logging in, setting privacy preferences, or filling out forms.
                    </p>
                    <p className="text-xs text-gray-600">
                      Examples: Authentication, security, session management
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="w-12 h-6 bg-teal-500 rounded-full cursor-not-allowed opacity-50 relative">
                      <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full" />
                    </div>
                    <p className="text-xs text-gray-500 mt-1 text-center">Always On</p>
                  </div>
                </div>
              </div>

              {/* Functional Cookies */}
              <div className="mb-6 pb-6 border-b border-gray-200">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-900 mb-2">Functional Cookies</h4>
                    <p className="text-sm text-gray-700 mb-2">
                      These cookies enable enhanced functionality and personalization, such as remembering your preferences, saved locations, and personalized recommendations.
                    </p>
                    <p className="text-xs text-gray-600">
                      Examples: Language preferences, saved filters, customized views
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <button
                      onClick={() => handleToggle('functional')}
                      className={`w-12 h-6 rounded-full transition-colors ${
                        preferences.functional ? 'bg-teal-500' : 'bg-gray-300'
                      } relative`}
                    >
                      <div
                        className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                          preferences.functional ? 'translate-x-7' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="mb-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-900 mb-2">Analytics Cookies</h4>
                    <p className="text-sm text-gray-700 mb-2">
                      These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve our service for you.
                    </p>
                    <p className="text-xs text-gray-600">
                      Examples: Page views, feature usage, error tracking (anonymized)
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <button
                      onClick={() => handleToggle('analytics')}
                      className={`w-12 h-6 rounded-full transition-colors ${
                        preferences.analytics ? 'bg-teal-500' : 'bg-gray-300'
                      } relative`}
                    >
                      <div
                        className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                          preferences.analytics ? 'translate-x-7' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              {/* Notice */}
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded-r-lg">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-800">
                      <strong>Note:</strong> Disabling certain cookies may impact your experience and limit some features. You can change your preferences at any time in your account settings.
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleSavePreferences}
                  className="flex-1 px-6 py-3 bg-teal-500 hover:bg-teal-600 text-white font-semibold rounded-lg transition-colors shadow-md hover:shadow-lg"
                >
                  Save My Preferences
                </button>

                <button
                  onClick={handleAcceptAll}
                  className="flex-1 px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-lg transition-colors"
                >
                  Accept All
                </button>
              </div>

              {/* Legal Links */}
              <div className="mt-4 pt-4 border-t border-gray-200 text-center">
                <p className="text-xs text-gray-600">
                  For more information, read our{' '}
                  <Link to="/privacy" className="text-teal-600 hover:underline font-medium">
                    Privacy Policy
                  </Link>
                  {' '}and{' '}
                  <Link to="/terms" className="text-teal-600 hover:underline font-medium">
                    Terms of Service
                  </Link>
                  .
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
